// src/modules/PackageManager/PackageMappingList.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Search, Filter, RefreshCw, Package, CheckCircle, XCircle, Edit2, Download, Upload, Building2 } from 'lucide-react';
import {
  fetchPackageMappingList,
  updatePackageMapping,
  bulkCreatePackageMappings,
  validateBulkUploadJSON,
  FilterType,
  PackageIdentifierMapping,
  BulkUploadValidationResult,
  getPackageSourceLabel,
  getPackageSourceColor,
  bulkVerifyPackageMappings,
  updateExternalVendors,
  ExternalVendor,
} from '../../components/services/PackageManager';
import Pagination from '../../components/Pagination/Pagination';
import moment from 'moment';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import EditPackageMappingModal from './EditPackageMappingModal';
import EditExternalVendorsModal from './EditExternalVendorsModal';
import { downloadJSON } from '../../common/Utils/downloadHelpers';

const PackageMappingList: React.FC = () => {
  const { addToast } = useToast();
  const [mappings, setMappings] = useState<PackageIdentifierMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<PackageIdentifierMapping | null>(null);
  const [showExternalVendorsModal, setShowExternalVendorsModal] = useState(false);
  const [selectedExternalVendorsMapping, setSelectedExternalVendorsMapping] = useState<PackageIdentifierMapping | null>(null);
  const [externalVendorsSaving, setExternalVendorsSaving] = useState(false);

  // Filtering & Search
  const [filterType, setFilterType] = useState<FilterType>('Unmapped');
  const [searchValue, setSearchValue] = useState('');
  const [tempSearchValue, setTempSearchValue] = useState('');
  const [hoursRange, setHoursRange] = useState<number>(24); // New state for hours range

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [tokenHistory, setTokenHistory] = useState<(string | null)[]>([null]);

  // Bulk Upload State
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [validationResult, setValidationResult] = useState<BulkUploadValidationResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Bulk Action State
  const [bulkAction, setBulkAction] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Add this state for tracking bulk action submission
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  /* ------------------------------------------------------------------
     Checkbox Style Helper
  ------------------------------------------------------------------ */
  const getCheckboxStyle = (checked: boolean) => {
    return {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      border: checked ? '2px solid #00FF00' : '2px solid #444',
      backgroundColor: checked ? '#00FF00' : 'transparent',
      cursor: 'pointer',
      display: 'inline-block',
    };
  };

  /* ------------------------------------------------------------------
     Fetch Data
  ------------------------------------------------------------------ */
  const fetchData = useCallback(
    async (token: string | null = null) => {
      try {
        setLoading(true);

        const params: any = {
          filterType,
          pageSize,
        };

        // Handle filter values based on filter type
        if (filterType === 'Unmapped' || filterType === 'ByIsVerified' || filterType === 'ByNotVerified') {
          // No filterValue needed for these
          if (filterType === 'ByIsVerified') {
            params.filterValue = 'true';
          } else if (filterType === 'ByNotVerified') {
            params.filterType = 'ByIsVerified'; // Use ByIsVerified with false value
            params.filterValue = 'false';
          }
        } else if (filterType === 'Recent') {
          params.filterValue = searchValue || '24';
        } else if (filterType === 'ByIsDefatomManaged') {
          params.filterValue = searchValue || 'true';
        } else {
          params.filterValue = searchValue;
        }

        if (token) {
          params.continuationToken = token;
        }

        const response = await fetchPackageMappingList(params);

        if (response.isSuccess && response.data) {
          setMappings(response.data.items || []);
          setContinuationToken(response.data.continuationToken);
          setHasMoreResults(response.data.hasMoreResults);
        } else {
          addToast(response.message || 'Failed to fetch package mappings', 'error', 5000, 'Fetch Failed');
          setMappings([]);
        }
      } catch (err: any) {
        addToast(err.message || 'An error occurred while fetching data', 'error', 5000, 'Request Failed');
        setMappings([]);
      } finally {
        setLoading(false);
      }
    },
    [filterType, searchValue, pageSize, addToast],
  );

  /* ------------------------------------------------------------------
     Bulk Action Handlers
  ------------------------------------------------------------------ */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(mappings.map((m) => m.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  // Add this function after the other handlers
  const handleBulkVerify = async (registryDisplayNames: string[]) => {
    try {
      setBulkActionLoading(true);

      const payload = {
        registryDisplayNames,
      };

      // Call the bulk verify API using the service function
      const response = await bulkVerifyPackageMappings(payload);

      if (response.isSuccess) {
        addToast(`Successfully verified ${registryDisplayNames.length} packages`, 'success', 3000, 'Verification Complete');

        // Clear selections and reset bulk action
        setSelectedIds(new Set());
        setBulkAction('');

        // Refresh the data
        fetchData(tokenHistory[currentPage - 1] || null);
      } else {
        addToast(response.message || 'Failed to verify packages', 'error', 5000, 'Verification Failed');
      }
    } catch (error: any) {
      addToast(error.message || 'An error occurred during verification', 'error', 5000, 'Request Failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Update the existing handleBulkActionSubmit function
  const handleBulkActionSubmit = () => {
    if (!bulkAction) {
      addToast('Please select a bulk action', 'warning', 3000, 'No Action Selected');
      return;
    }

    if (selectedIds.size === 0) {
      addToast('Please select at least one item', 'warning', 3000, 'No Items Selected');
      return;
    }

    // Get the registry display names from selected mappings
    const selectedMappings = mappings.filter((m) => selectedIds.has(m.id));
    const registryDisplayNames = selectedMappings
      .map((m) => m.registryDisplayName)
      .filter((name): name is string => name !== undefined && name !== null);

    if (registryDisplayNames.length === 0) {
      addToast('No valid display names found for selected items', 'warning', 3000, 'No Display Names');
      return;
    }

    if (bulkAction === 'verify') {
      handleBulkVerify(registryDisplayNames);
    }

    // TODO: Add more bulk actions here as needed
  };

  /* ------------------------------------------------------------------
     Handle Edit
  ------------------------------------------------------------------ */
  const handleEdit = (mapping: PackageIdentifierMapping) => {
    setSelectedMapping(mapping);
    setShowEditModal(true);
  };

  const handleEditExternalVendors = (mapping: PackageIdentifierMapping) => {
    setSelectedExternalVendorsMapping(mapping);
    setShowExternalVendorsModal(true);
  };

  const handleSaveEdit = async (packageIdentifier: string, isDefatomManaged: boolean, isVerified: boolean, notes: string) => {
    if (!selectedMapping) return;

    try {
      setSaving(true);

      const payload = {
        packageIdentifier,
        isDefatomManaged,
        isVerified,
        notes,
      };

      const response = await updatePackageMapping(selectedMapping.id, payload);

      if (response.isSuccess) {
        addToast('Package mapping updated successfully', 'success', 3000, 'Update Successful');

        // Update the local state
        setMappings((prev) =>
          prev.map((m) =>
            m.id === selectedMapping.id
              ? { ...m, packageIdentifier, isDefatomManaged, isVerified, notes, updatedAt: new Date().toISOString() }
              : m,
          ),
        );

        setShowEditModal(false);
        setSelectedMapping(null);

        // Refresh the data
        fetchData(tokenHistory[currentPage - 1] || null);
      } else {
        // addToast(response.message || 'Failed to update package mapping', 'error', 5000, 'Update Failed');
      }
    } catch (err: any) {
      // addToast(err.message || 'An error occurred while updating', 'error', 5000, 'Request Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExternalVendors = async (packageIdentifier: string, externalVendors: ExternalVendor[] | null) => {
    if (!selectedExternalVendorsMapping) {
      return;
    }

    try {
      setExternalVendorsSaving(true);

      const payload = {
        packageIdentifier,
        externalVendors,
      };

      const response = await updateExternalVendors(payload);

      if (response.isSuccess) {
        addToast('External vendors updated successfully', 'success', 3000, 'Update Successful');

        setMappings((prev) =>
          prev.map((item) =>
            item.id === selectedExternalVendorsMapping.id
              ? {
                  ...item,
                  externalVendors,
                  updatedAt: new Date().toISOString(),
                }
              : item,
          ),
        );

        setShowExternalVendorsModal(false);
        setSelectedExternalVendorsMapping(null);

        fetchData(tokenHistory[currentPage - 1] || null);
      } else {
        addToast(response.message || 'Failed to update external vendors', 'error', 5000, 'Update Failed');
      }
    } catch (error: any) {
      addToast(error.message || 'An error occurred while updating external vendors', 'error', 5000, 'Request Failed');
    } finally {
      setExternalVendorsSaving(false);
    }
  };

  /* ------------------------------------------------------------------
     Initial Load & Filter/Search Changes
  ------------------------------------------------------------------ */
  useEffect(() => {
    // Reset pagination when filter or search changes
    setCurrentPage(1);
    setTokenHistory([null]);
    setContinuationToken(null);
    setSelectedIds(new Set()); // Clear selections on filter change
    fetchData(null);
  }, [filterType, searchValue, pageSize, fetchData]);

  /* ------------------------------------------------------------------
     Pagination Handlers
  ------------------------------------------------------------------ */
  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;

    if (newPage > currentPage) {
      // Next page
      if (continuationToken) {
        const newHistory = [...tokenHistory, continuationToken];
        setTokenHistory(newHistory);
        setCurrentPage(newPage);
        setSelectedIds(new Set()); // Clear selections on page change
        fetchData(continuationToken);
      }
    } else if (newPage < currentPage) {
      // Previous page
      const tokenForPage = tokenHistory[newPage - 1] || null;
      setCurrentPage(newPage);
      setSelectedIds(new Set()); // Clear selections on page change
      fetchData(tokenForPage);
    } else {
      // Same page (reload)
      const tokenForPage = tokenHistory[newPage - 1] || null;
      fetchData(tokenForPage);
    }
  };

  /* ------------------------------------------------------------------
     Search Handler
  ------------------------------------------------------------------ */
  const handleSearch = () => {
    if (filterType === 'Recent') {
      setSearchValue(hoursRange.toString());
    } else if (currentFilter?.needsInput && !tempSearchValue.trim()) {
      addToast('Please enter a search value', 'warning', 3000, 'Search Required');
      return;
    } else {
      setSearchValue(tempSearchValue);
    }
  };

  const handleFilterChange = (newFilterType: FilterType) => {
    setFilterType(newFilterType);
    setTempSearchValue('');
    setSearchValue('');
    if (newFilterType === 'Recent') {
      setHoursRange(24); // Reset to default 24 hours
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  /* ------------------------------------------------------------------
     Download Handler
  ------------------------------------------------------------------ */
  const handleDownloadJSON = () => {
    if (mappings.length === 0) {
      addToast('No data to download', 'warning', 3000, 'Download');
      return;
    }

    const success = downloadJSON(mappings, `package-mappings-${filterType}-page-${currentPage}`);

    if (success) {
      addToast(`Downloaded ${mappings.length} records as JSON`, 'success', 3000, 'Download Complete');
    } else {
      addToast('Failed to download JSON', 'error', 3000, 'Download Failed');
    }
  };

  /* ------------------------------------------------------------------
     Bulk Upload Handlers
  ------------------------------------------------------------------ */
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.json')) {
      addToast('Please select a JSON file', 'error', 3000, 'Invalid File Type');
      return;
    }

    setSelectedFile(file);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate the JSON structure
      const result = validateBulkUploadJSON(data);
      setValidationResult(result);

      if (result.isValid) {
        addToast(
          `File validated successfully! ${result.validMappings.length} mappings ready to upload`,
          'success',
          3000,
          'Validation Successful',
        );
        if (result.warnings.length > 0) {
          addToast(`${result.warnings.length} warning(s) found`, 'warning', 5000, 'Warnings');
        }
      } else {
        addToast(`Validation failed: ${result.errors.length} error(s) found`, 'error', 5000, 'Validation Failed');
      }
    } catch (error: any) {
      addToast('Failed to parse JSON file: ' + error.message, 'error', 5000, 'Parse Error');
      setValidationResult(null);
      setSelectedFile(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!validationResult || !validationResult.isValid || validationResult.validMappings.length === 0) {
      addToast('No valid mappings to upload', 'warning', 3000, 'Upload Failed');
      return;
    }

    try {
      setUploadingBulk(true);

      const payload = {
        mappings: validationResult.validMappings,
      };

      const response = await bulkCreatePackageMappings(payload);

      if (response.isSuccess) {
        addToast(`Successfully uploaded ${validationResult.validMappings.length} package mappings`, 'success', 5000, 'Upload Successful');
        setShowBulkUploadModal(false);
        setSelectedFile(null);
        setValidationResult(null);

        // Refresh the data
        fetchData(null);
      } else {
        addToast(response.message || 'Failed to upload package mappings', 'error', 5000, 'Upload Failed');
      }
    } catch (error: any) {
      addToast(error.message || 'An error occurred during upload', 'error', 5000, 'Request Failed');
    } finally {
      setUploadingBulk(false);
    }
  };

  const resetBulkUpload = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setShowBulkUploadModal(false);
  };

  /* ------------------------------------------------------------------
     Calculate total pages
  ------------------------------------------------------------------ */
  const calculateTotalPages = (): number => {
    // Since we use continuation tokens, we estimate pages
    if (hasMoreResults) {
      return currentPage + 1; // At least one more page
    }
    return currentPage;
  };

  const totalPages = calculateTotalPages();

  /* ------------------------------------------------------------------
     Filter Type Options
  ------------------------------------------------------------------ */
  const filterOptions: { value: FilterType; label: string; needsInput: boolean; inputType?: string; placeholder: string }[] = [
    { value: 'Unmapped', label: 'Unmapped', needsInput: false, placeholder: '' },
    { value: 'ByIsVerified', label: 'Verified Packages', needsInput: false, placeholder: '' },
    { value: 'ByNotVerified', label: 'UnVerified Packages', needsInput: false, placeholder: '' },
    { value: 'Recent', label: 'Recent (hours)', needsInput: true, inputType: 'range', placeholder: 'Select hours...' },
    { value: 'ByName', label: 'By Name', needsInput: true, inputType: 'text', placeholder: 'Search name...' },
    { value: 'ByPublisher', label: 'By Publisher', needsInput: true, inputType: 'text', placeholder: 'Search publisher...' },
    // { value: 'ByProductCode', label: 'By Product Code', needsInput: true, inputType: 'text', placeholder: 'Exact product code...' },
    // { value: 'ByUpgradeCode', label: 'By Upgrade Code', needsInput: true, inputType: 'text', placeholder: 'Exact upgrade code...' },
    { value: 'ByPackageIdentifier', label: 'By Package ID', needsInput: true, inputType: 'text', placeholder: 'Search package ID...' },
    { value: 'ByIsDefatomManaged', label: 'Defatom Managed', needsInput: false, inputType: 'text', placeholder: 'true or false' },
  ];

  // Hours range options: 1 hour to 1 month
  const hoursOptions = [
    { label: '1 hour', value: 1 },
    { label: '3 hours', value: 3 },
    { label: '6 hours', value: 6 },
    { label: '12 hours', value: 12 },
    { label: '24 hours (1 day)', value: 24 },
    { label: '2 days', value: 48 },
    { label: '3 days', value: 72 },
    { label: '1 week', value: 168 },
    { label: '2 weeks', value: 336 },
    { label: '1 month', value: 720 },
  ];
  const currentFilter = filterOptions.find((f) => f.value === filterType);

  const isAllSelected = mappings.length > 0 && selectedIds.size === mappings.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < mappings.length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package className="text-red-500" size={28} />
            Package Mapping Manager
          </h1>
          <p className="text-gray-400 text-sm mt-1">Manage and view package identifier mappings</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-all"
          >
            <Upload size={16} />
            Bulk Upload
          </button>
          <button
            onClick={() => fetchData(tokenHistory[currentPage - 1] || null)}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <RefreshCw className={loading ? 'animate-spin' : ''} size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-[#1C1C1D] p-4 rounded-lg border border-gray-700 space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Filter Type Selector */}
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value as FilterType)}
              className="px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 transition-all"
            >
              {filterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input (conditional - only for filters that need input) */}
          {/* {currentFilter?.needsInput && filterType === 'Recent' && (
            <div className="flex items-center gap-2 flex-1 max-w-lg">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-400">Hours Range:</label>
                  <span className="text-sm text-red-400 font-semibold">{hoursRange} hours</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="720"
                  value={hoursRange}
                  onChange={(e) => setHoursRange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1h</span>
                  <span>1 month</span>
                </div>
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all self-end"
              >
                Apply
              </button>
            </div>
          )} */}

          {/* Text Search Input (for other filters) */}
          {currentFilter?.needsInput && filterType !== 'Recent' && (
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={tempSearchValue}
                  onChange={(e) => setTempSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentFilter.placeholder}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 transition-all"
                />
              </div>
              <button onClick={handleSearch} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all">
                Search
              </button>
            </div>
          )}

          {/* Quick preset buttons for hours (optional) */}
          {currentFilter?.needsInput && filterType === 'Recent' && (
            <div className="flex items-center gap-2 flex-wrap">
              {hoursOptions.slice(0, 5).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setHoursRange(opt.value);
                    setSearchValue(opt.value.toString());
                  }}
                  className={`px-3 py-1 rounded text-sm transition-all ${
                    hoursRange === opt.value ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {opt.label.split(' ')[0]}Hr
                </button>
              ))}
            </div>
          )}
          {/* Bulk Action, Submit, Page Size & Download Button */}
          <div className="flex items-center gap-3 ml-auto">
            {/* Bulk Action Selector */}
            <div className="flex items-center gap-2">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 transition-all"
              >
                <option value="" disabled selected hidden>
                  Bulk Action
                </option>
                <option value="verify">Mark As Verified</option>
              </select>

              {/* Submit Button */}
              <button
                onClick={handleBulkActionSubmit}
                disabled={!bulkAction || selectedIds.size === 0 || bulkActionLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all flex items-center gap-2"
              >
                {bulkActionLoading ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    Verifying...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>

            <span className="text-gray-400 text-sm">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 transition-all"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            {/* Download JSON Button */}
            <button
              onClick={handleDownloadJSON}
              disabled={loading || mappings.length === 0}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-all"
              title="Download current page as JSON"
            >
              <Download size={16} />
              Download (JSON)
            </button>
          </div>
        </div>

        {/* Active Filter Info */}
        {searchValue && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400">Active filter:</span>
            <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded">
              {currentFilter?.label}: "{searchValue}"
            </span>
            <button
              onClick={() => {
                setSearchValue('');
                setTempSearchValue('');
                addToast('Filter cleared', 'info', 2000);
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XCircle size={16} />
            </button>
          </div>
        )}

        {/* Selected Count */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400">Selected:</span>
            <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded font-semibold">{selectedIds.size} item(s)</span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-gray-400 hover:text-white transition-colors text-xs underline"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="text-gray-400 text-sm">
        {loading ? (
          'Loading...'
        ) : (
          <>
            Showing {mappings.length} results on page {currentPage}
            {hasMoreResults && ' (more available)'}
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#1C1D1E] rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-gray-300">
            <thead className="bg-[#252525] border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">
                  <div
                    onClick={() => handleSelectAll(!isAllSelected)}
                    style={{
                      ...getCheckboxStyle(isAllSelected),
                      position: 'relative',
                    }}
                  >
                    {isAllSelected && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {isSomeSelected && !isAllSelected && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '8px',
                          height: '2px',
                          backgroundColor: 'white',
                        }}
                      />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left font-semibold">Package Identifier</th>
                <th className="px-4 py-3 text-left font-semibold">Display Name</th>
                <th className="px-4 py-3 text-left font-semibold">Publisher</th>
                <th className="px-4 py-3 text-left font-semibold">Product Code</th>
                <th className="px-4 py-3 text-left font-semibold">Verified</th>
                <th className="px-4 py-3 text-left font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex justify-center items-center gap-2">
                      <RefreshCw className="animate-spin text-red-500" size={24} />
                      <span className="text-gray-400">Loading mappings...</span>
                    </div>
                  </td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Package className="text-gray-600" size={48} />
                      <span className="text-gray-400">No mappings found</span>
                    </div>
                  </td>
                </tr>
              ) : (
                mappings.map((mapping, idx) => (
                  <tr key={mapping.id || idx} className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors group">
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <div
                        onClick={() => handleSelectOne(mapping.id, !selectedIds.has(mapping.id))}
                        style={getCheckboxStyle(selectedIds.has(mapping.id))}
                      ></div>
                    </td>

                    {/* Package Identifier */}
                    <td className="px-4 py-3">
                      {mapping.packageIdentifier ? (
                        <span className="font-mono text-xs text-red-400">{mapping.packageIdentifier}</span>
                      ) : (
                        <span className="text-gray-500 italic">-</span>
                      )}
                    </td>

                    {/* Display Name */}
                    <td className="px-4 py-3 max-w-xs truncate" title={mapping.registryDisplayName}>
                      {mapping.registryDisplayName || '-'}
                    </td>

                    {/* Publisher */}
                    <td className="px-4 py-3 max-w-xs truncate" title={mapping.registryPublisher}>
                      {mapping.registryPublisher || '-'}
                    </td>

                    {/* Product Code */}
                    <td className="px-4 py-3 max-w-xs truncate font-mono text-xs" title={mapping.productCode}>
                      {mapping.productCode || '-'}
                    </td>

                    {/* Verified */}
                    <td className="px-4 py-3">
                      {mapping.isVerified ? (
                        <span className="px-2 py-0.5 bg-red-900/30 text-red-400 rounded text-xs">Verified</span>
                      ) : (
                        <span className="text-gray-500 italic">Unverified</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(mapping)}
                          className="p-2 rounded-lg bg-gray-700 hover:bg-red-900/30 hover:border-red-500 border border-transparent transition-all group-hover:scale-105"
                          title="Edit Package Mapping"
                        >
                          <Edit2 className="text-gray-400 hover:text-red-400 transition-colors" size={16} />
                        </button>

                        {filterType === 'ByIsDefatomManaged' && (
                          <button
                            onClick={() => handleEditExternalVendors(mapping)}
                            disabled={!mapping.packageIdentifier}
                            className="p-2 rounded-lg bg-gray-700 hover:bg-red-900/30 hover:border-red-500 border border-transparent transition-all group-hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={mapping.packageIdentifier ? 'Add or Edit External Vendors' : 'Package Identifier is required'}
                          >
                            <Building2 className="text-gray-400 hover:text-red-400 transition-colors" size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && mappings.length > 0 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
      )}

      {/* Edit Modal */}
      <EditPackageMappingModal
        show={showEditModal}
        mapping={selectedMapping}
        onClose={() => {
          setShowEditModal(false);
          setSelectedMapping(null);
        }}
        onSave={handleSaveEdit}
        saving={saving}
      />

      <EditExternalVendorsModal
        show={showExternalVendorsModal}
        mapping={selectedExternalVendorsMapping}
        saving={externalVendorsSaving}
        onClose={() => {
          setShowExternalVendorsModal(false);
          setSelectedExternalVendorsMapping(null);
        }}
        onSave={handleSaveExternalVendors}
      />

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1C1C1D] rounded-lg border border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#1C1C1D] border-b border-gray-700 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Upload className="text-red-500" size={24} />
                <h2 className="text-xl font-bold text-white">Bulk Upload Package Mappings</h2>
              </div>
              <button onClick={resetBulkUpload} className="text-gray-400 hover:text-white transition-colors">
                <XCircle size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* File Upload Section */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Select JSON File</label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 hover:border-red-500 transition-colors text-center">
                      <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                      <p className="text-sm text-gray-400">{selectedFile ? selectedFile.name : 'Click to select a JSON file'}</p>
                      <p className="text-xs text-gray-500 mt-1">File must contain packageIdentifier and registryDisplayName</p>
                    </div>
                    <input type="file" accept=".json" onChange={handleFileSelect} className="hidden" />
                  </label>
                </div>
              </div>

              {/* Validation Results */}
              {validationResult && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-gray-800/50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Items:</span>
                      <span className="text-white font-semibold">
                        {validationResult.validMappings.length + validationResult.errors.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Valid Mappings:</span>
                      <span className="text-green-400 font-semibold">{validationResult.validMappings.length}</span>
                    </div>
                    {validationResult.errors.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Errors:</span>
                        <span className="text-red-400 font-semibold">{validationResult.errors.length}</span>
                      </div>
                    )}
                    {validationResult.warnings.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Warnings:</span>
                        <span className="text-yellow-400 font-semibold">{validationResult.warnings.length}</span>
                      </div>
                    )}
                  </div>

                  {/* Errors */}
                  {validationResult.errors.length > 0 && (
                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <XCircle className="text-red-400" size={20} />
                        <h3 className="font-semibold text-red-400">Validation Errors</h3>
                      </div>
                      <ul className="space-y-1 text-sm text-red-300 max-h-40 overflow-auto">
                        {validationResult.errors.map((error, idx) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {validationResult.warnings.length > 0 && (
                    <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-yellow-400">⚠️</span>
                        <h3 className="font-semibold text-yellow-400">Warnings</h3>
                      </div>
                      <ul className="space-y-1 text-sm text-yellow-300 max-h-40 overflow-auto">
                        {validationResult.warnings.slice(0, 5).map((warning, idx) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                        {validationResult.warnings.length > 5 && (
                          <li className="text-yellow-400 font-medium">... and {validationResult.warnings.length - 5} more warnings</li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Valid Mappings Preview */}
                  {validationResult.isValid && validationResult.validMappings.length > 0 && (
                    <div className="bg-green-900/20 border border-green-500/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="text-green-400" size={20} />
                        <h3 className="font-semibold text-green-400">Ready to Upload ({validationResult.validMappings.length} items)</h3>
                      </div>
                      <div className="text-sm text-gray-300 max-h-60 overflow-auto">
                        <p className="mb-2">Preview (first 3 items):</p>
                        {validationResult.validMappings.slice(0, 3).map((mapping, idx) => (
                          <div key={idx} className="bg-gray-800/50 rounded p-2 mb-2">
                            <div className="font-mono text-xs text-red-400">{mapping.packageIdentifier}</div>
                            <div className="text-gray-300">{mapping.registryDisplayName}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#1C1C1D] border-t border-gray-700 p-6 flex justify-end gap-3">
              <button
                onClick={resetBulkUpload}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                disabled={uploadingBulk}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!validationResult || !validationResult.isValid || uploadingBulk}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all flex items-center gap-2"
              >
                {uploadingBulk ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload {validationResult?.validMappings.length || 0} Mappings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageMappingList;
